import { safeJsonParse, safeParseStorage } from './helpers';
import { DEFAULT_LOCALE as DEFAULT_LOCALE_VALUE, isSupportedLocale } from './localeConfig';

// ============================================
// AUTENTICACIÓN Y GESTIÓN DE TOKENS
// ============================================

export function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return safeJsonParse(jsonPayload, null);
  } catch (e) {
    return null;
  }
}

export function getUserFromToken() {
  const token = sessionStorage.getItem('token');
  if (!token) return null;
  return parseJwt(token);
}

// ============================================
// ROL Y SUPERADMIN — FUENTE ÚNICA
// ============================================
// Rol de superadmin. Toda la app debe usar esta constante y los helpers de
// abajo en vez de comparar contra 3 a mano leyendo tipo_usuario/perfil_id/role_id.
export const SUPERADMIN_ROLE = 3;

// Lee el rol de un objeto proyecto/usuario tolerando los 3 nombres de campo
// que conviven en los datos (tipo_usuario | perfil_id | role_id).
function leerRol(obj) {
  const raw = obj?.tipo_usuario ?? obj?.perfil_id ?? obj?.role_id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * ¿El usuario tiene que reemplazar su contraseña antes de seguir?
 *
 * El backend marca `debe_cambiar_password` cuando la clave la fijó un tercero:
 * el alta de la cuenta (clave inicial generada por quien lo dio de alta) o el
 * reseteo de un administrador. Mientras esté en 1, la clave la conocen dos
 * personas. El guard del router lo manda a /mi-cuenta hasta que la cambie.
 */
export function debeCambiarPassword() {
  return Boolean(getUserFromToken()?.debe_cambiar_password);
}

/** Rol del usuario en el proyecto actualmente seleccionado (o null). */
export function getRolActual() {
  const user = getUserFromToken();
  if (!user) return null;
  const proyectoId = sessionStorage.getItem('proyectSelected');
  const proyecto = (user.proyectos || []).find((p) => String(p.id) === String(proyectoId));
  return leerRol(proyecto) ?? leerRol(user);
}

// ============================================
// FORMATO REGIONAL (LOCALE) POR PROYECTO
// ============================================
// Mismo patrón que getRolActual(): el locale asociado al proyecto viaja en
// el token (usuario.proyectos[].locale), igual que zona_horaria_utc.
//
// DEFAULT_LOCALE y SUPPORTED_LOCALES ya NO se definen acá: viven en
// utils/localeConfig.js (fuente única). Antes este archivo declaraba
// 'en-US' mientras formatProjectData.js declaraba 'es-CL'; la contradicción
// era invisible sólo porque el locale nunca llegaba a aplicarse. Se
// re-exportan para no romper imports existentes.
export { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './localeConfig';

// Huso horario del proyecto: mismo patrón que el locale, otro eje.
import {
  DEFAULT_TIMEZONE,
  isSupportedTimezone
} from './timezoneConfig';
export { DEFAULT_TIMEZONE, SUPPORTED_TIMEZONES } from './timezoneConfig';

// Clave de sessionStorage donde se guarda el Formato Regional recién
// guardado desde Administración > Proyectos. El locale "de verdad" viaja en
// el JWT, que no se puede re-firmar en el cliente; sin este override el
// usuario tendría que cerrar sesión para ver el cambio que acaba de hacer.
// Tiene prioridad sobre el token y se limpia en clearAuthContext().
const LOCALE_OVERRIDE_KEY = 'hb:locale-override';

function readLocaleOverrides() {
  return safeParseStorage(sessionStorage, LOCALE_OVERRIDE_KEY, {}) || {};
}

/**
 * Registra localmente el Formato Regional de un proyecto, para que el cambio
 * sea visible de inmediato sin re-login. El próximo token lo trae ya correcto
 * desde la BD y este override deja de tener efecto práctico.
 */
export function setLocaleOverride(proyectoId, locale) {
  if (proyectoId == null) return;
  try {
    const overrides = readLocaleOverrides();
    if (isSupportedLocale(locale)) overrides[String(proyectoId)] = String(locale).trim();
    else delete overrides[String(proyectoId)];
    sessionStorage.setItem(LOCALE_OVERRIDE_KEY, JSON.stringify(overrides));
  } catch {
    // no-op: el override es una mejora de UX, nunca debe romper el guardado
  }
}

// (Se eliminaron clearLocaleOverrides() y clearTimezoneOverrides(): nadie las
// llamaba, y la limpieza real la hace clearAuthContext() por lista de claves.
// Además clearLocaleOverrides borraba TAMBIÉN el override de huso, un cruce que
// no se veía desde su nombre.)

/**
 * Formato regional del proyecto actualmente seleccionado, distinguiendo si
 * fue configurado explícitamente o si se está usando el default del sistema.
 * Esa distinción la usa la capa de presentación para decidir, por ejemplo,
 * si aplicar separador de miles.
 *
 * Orden de precedencia:
 *   1. Override local (recién guardado en Administración > Proyectos)
 *   2. proyectos[].locale del JWT (fuente real, viene de la BD)
 *   3. user.locale del JWT (compatibilidad)
 *   4. DEFAULT_LOCALE
 *
 * @returns {{ locale: string, configured: boolean }}
 */
export function getProjectLocaleConfig() {
  const user = getUserFromToken();
  const proyectoId = sessionStorage.getItem('proyectSelected');
  const proyecto = (user?.proyectos || []).find((p) => String(p.id) === String(proyectoId));
  const raw = readLocaleOverrides()[String(proyectoId)] || proyecto?.locale || user?.locale;
  const configured = isSupportedLocale(raw);
  return { locale: configured ? String(raw).trim() : DEFAULT_LOCALE_VALUE, configured };
}

/** Locale (formato regional) del proyecto actualmente seleccionado. */
export function getLocaleActual() {
  return getProjectLocaleConfig().locale;
}

// ---------------------------------------------------------------------------
// Huso horario del proyecto
// ---------------------------------------------------------------------------
// Mismo mecanismo que el Formato Regional, con una diferencia de fondo: el
// locale es presentación pura, así que equivocarlo se ve feo; el huso es
// INTERPRETACIÓN, así que equivocarlo muestra un dato incorrecto.

const TIMEZONE_OVERRIDE_KEY = 'hb:timezone-override';

function readTimezoneOverrides() {
  return safeParseStorage(sessionStorage, TIMEZONE_OVERRIDE_KEY, {}) || {};
}

/**
 * Registra localmente el huso de un proyecto recién guardado, para que el
 * cambio se vea sin re-login (el JWT no se puede re-firmar en el cliente).
 */
export function setTimezoneOverride(proyectoId, timezone) {
  if (proyectoId == null) return;
  try {
    const overrides = readTimezoneOverrides();
    if (isSupportedTimezone(timezone)) overrides[String(proyectoId)] = String(timezone).trim();
    else delete overrides[String(proyectoId)];
    sessionStorage.setItem(TIMEZONE_OVERRIDE_KEY, JSON.stringify(overrides));
  } catch {
    // no-op: el override es una mejora de UX, nunca debe romper el guardado
  }
}

/**
 * Huso horario del proyecto seleccionado.
 *
 * Precedencia:
 *   1. Override local (recién guardado en Administración > Proyectos)
 *   2. proyectos[].timezone del JWT (fuente real, viene de la BD)
 *   3. El listado cacheado de /general-access-user
 *   4. Derivado del modelo viejo (`zona_horaria` / `zona_horaria_utc`)
 *   5. DEFAULT_TIMEZONE
 *
 * >>> POR QUÉ EL PASO 3 NO ES OPCIONAL <<<
 * Un SUPERADMIN ve en el selector TODOS los proyectos activos, pero su token
 * sólo lleva sus membresías reales. Al elegir un proyecto del que no es
 * miembro, el token no lo tiene y antes se caía a offset 0 EN SILENCIO — o sea
 * ese proyecto se mostraba en UTC pasara lo que pasara. Verificado con
 * Paltos_SF (America/Denver).
 *
 * El paso 4 existe porque el JWT dura 24 h: durante un día conviven tokens
 * emitidos antes y después de la migración.
 *
 * @returns {{ timezone: string, configured: boolean }}
 */
export function getProjectTimezoneConfig() {
  const proyectoId = sessionStorage.getItem('proyectSelected');
  const clave = String(proyectoId);

  const override = readTimezoneOverrides()[clave];
  if (isSupportedTimezone(override)) {
    return { timezone: String(override).trim(), configured: true };
  }

  const user = getUserFromToken();
  const delToken = (user?.proyectos || []).find((p) => String(p.id) === clave);

  // >>> OJO CON EL NOMBRE DE LA CLAVE <<<
  // La lista de proyectos vive con DOS nombres distintos, y no son intercambiables:
  //
  //   localStorage   'hb:proyectos'   (persistente, sobrevive al cierre del navegador)
  //   sessionStorage 'proyectos'      (sin prefijo; lo copia el bootstrap de sesion)
  //
  // Este nivel de la cadena leia 'hb:proyectos' EN sessionStorage — una clave que
  // no existe nunca. El nivel entero era codigo muerto: cualquier proyecto que no
  // estuviera en el JWT caia al huso por defecto EN SILENCIO. Se nota con un
  // superadmin, que ve en el selector proyectos de los que no es miembro: Paltos_SF
  // (America/Denver) se mostraba en hora de Chile.
  let cacheado = null;
  try {
    const crudo = safeParseStorage(sessionStorage, 'proyectos', null)
      ?? safeParseStorage(localStorage, 'hb:proyectos', null);
    const lista = Array.isArray(crudo) ? crudo : (crudo?.data ?? crudo?.proyectos ?? []);
    cacheado = (Array.isArray(lista) ? lista : []).find((p) => String(p?.id) === clave) || null;
  } catch {
    cacheado = null;
  }

  const candidato = delToken?.timezone || cacheado?.timezone;
  if (isSupportedTimezone(candidato)) {
    return { timezone: String(candidato).trim(), configured: true };
  }

  // (Aquí había un cuarto nivel que derivaba el huso del trío viejo
  //  `zona_horaria` / `zona_horaria_utc`. Esas columnas ya no existen y el token
  //  no las trae: si `timezone` no resuelve, se cae al default y `configured`
  //  queda en false, que es la señal de que nadie lo configuró.)
  return { timezone: DEFAULT_TIMEZONE, configured: false };
}

/** Huso horario del proyecto actualmente seleccionado. */
export function getTimezoneActual() {
  return getProjectTimezoneConfig().timezone;
}

/**
 * Superadmin GLOBAL: lo es si su rol es 3 a nivel de usuario o en CUALQUIER
 * proyecto. No depende del proyecto seleccionado (decisión de diseño: el
 * superadmin ve todo, siempre).
 */
export function esSuperAdmin(user = null) {
  const u = user || getUserFromToken();
  if (!u) return false;
  if (leerRol(u) === SUPERADMIN_ROLE) return true;
  return Array.isArray(u.proyectos) && u.proyectos.some((p) => leerRol(p) === SUPERADMIN_ROLE);
}

// --- Compatibilidad: las funciones previas delegan en el núcleo único ---
export function getUserTypeFromProyectos(proyectos, proyectoId) {
  const p = (proyectos || []).find((x) => String(x.id) === String(proyectoId));
  return leerRol(p);
}

export function getUserTypeFallbackFromToken() {
  return leerRol(getUserFromToken());
}

export function getPerfilSession() {
  return getRolActual();
}

// ============================================
// TOKEN COMPARTIDO ENTRE PESTAÑAS (CON EXPIRACIÓN)
// ============================================
// El token vive en sessionStorage (por pestaña). Para que ventanas nuevas no
// pidan login, se guarda una copia en localStorage con timestamp que caduca a
// las SHARED_TOKEN_TTL_MS. Así una sesión olvidada no queda abierta para
// siempre en un equipo compartido.

export const SHARED_TOKEN_TTL_MS = 8 * 60 * 60 * 1000; // 8 horas

export function persistSharedToken(token) {
  if (!token) return;
  try {
    localStorage.setItem('hb:token', JSON.stringify({ token, ts: Date.now() }));
  } catch {
    // no-op
  }
}

export function readSharedToken() {
  const data = safeParseStorage(localStorage, 'hb:token', null);
  if (!data?.token || !data?.ts) return null;
  if (Date.now() - Number(data.ts) > SHARED_TOKEN_TTL_MS) {
    localStorage.removeItem('hb:token');
    return null;
  }
  return data.token;
}

// ============================================
// HIDRATACIÓN TEMPRANA (token + proyecto + menú)
// ============================================

export function earlyHydration() {
  try {
    // Token: restaurar desde la copia compartida si sigue vigente
    const tk = sessionStorage.getItem('token') || readSharedToken();
    if (tk && !sessionStorage.getItem('token')) {
      sessionStorage.setItem('token', tk);
    }

    // Proyecto seleccionado
    if (!sessionStorage.getItem('proyectSelected')) {
      const last = safeParseStorage(localStorage, 'hb:last-project', null);
      if (last?.id) {
        sessionStorage.setItem('proyectSelected', String(last.id));
        if (last.name) sessionStorage.setItem('proyectSelectedName', last.name);
        if (last.menu) sessionStorage.setItem('menu', JSON.stringify(last.menu));
      }
    }

    // Proyectos
    const cachedProyectos = localStorage.getItem('hb:proyectos');
    if (cachedProyectos && !sessionStorage.getItem('proyectos')) {
      sessionStorage.setItem('proyectos', cachedProyectos);
    }
  } catch {
    // no-op
  }
}

export function ensureProjectHydrated() {
  if (!sessionStorage.getItem('proyectSelected')) {
    const last = safeParseStorage(localStorage, 'hb:last-project', null);
    if (last?.id) {
      sessionStorage.setItem('proyectSelected', String(last.id));
      if (last.name) sessionStorage.setItem('proyectSelectedName', last.name);
      if (last.menu) sessionStorage.setItem('menu', JSON.stringify(last.menu));
    }
  }
}

// ============================================
// SINCRONIZACIÓN ENTRE PESTAÑAS (STORAGE EVENTS)
// ============================================

export function setupCrossTabSync() {
  const handleStorageChange = (e) => {
    // Sincronización de autenticación
    if (e.key === 'hb:auth-sync' && e.newValue) {
      try {
        const msg = safeJsonParse(e.newValue, null);
        if (!msg) return;
        if (msg.type === 'login' && msg.token) {
          sessionStorage.setItem('token', msg.token);
          localStorage.setItem('hb:last-auth', e.newValue);
          window.location.href = '/home';
        }
        if (msg.type === 'logout') {
          clearAuthContext();
          window.dispatchEvent(new Event('hb:session-updated'));
          window.location.href = '/login';
        }
      } catch {
        // no-op
      }
    }

    // Sincronización de proyecto
    if (e.key === 'hb:project-sync' && e.newValue) {
      try {
        const msg = safeJsonParse(e.newValue, null);
        if (!msg) return;
        if (msg.type === 'logout') {
          sessionStorage.removeItem('proyectSelected');
          sessionStorage.removeItem('menu');
        } else {
          sessionStorage.setItem('proyectSelected', msg.id);
          if (msg.name) sessionStorage.setItem('proyectSelectedName', msg.name);
          if (msg.menu) sessionStorage.setItem('menu', JSON.stringify(msg.menu));
        }
        // Cambió el proyecto activo en otra pestaña: el Formato Regional
        // puede ser distinto. utils/localeBootstrap escucha este evento y
        // re-aplica el locale (números, Day.js y gráficos).
        window.dispatchEvent(new Event('hb:session-updated'));
      } catch {
        // no-op
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
}

// ============================================
// LIMPIEZA DE SESION/CACHE DE AUTENTICACION
// ============================================

export function clearAuthContext({ preserveToken = false } = {}) {
  const sessionKeys = [
    'token',
    'menu',
    'proyectos',
    'proyectSelected',
    'proyectSelectedName',
    'notifications',
    'hb:next',
    // Overrides guardados localmente a la espera del próximo token. LOS DOS
    // tienen que estar acá: son el nivel de MÁS prioridad de sus cadenas de
    // precedencia, así que un override que sobrevive al logout le gana al JWT
    // del usuario siguiente. El del huso faltaba: quien editara un proyecto y
    // cerrara sesión dejaba esa zona pegada para el próximo que entrara en la
    // misma pestaña, sin ningún aviso.
    'hb:locale-override',
    'hb:timezone-override'
  ];

  const localKeys = [
    'token',
    'hb:token',
    'hb:last-project',
    'hb:proyectos',
    'hb:last-auth'
  ];

  for (const key of sessionKeys) {
    if (preserveToken && key === 'token') continue;
    sessionStorage.removeItem(key);
  }

  for (const key of localKeys) {
    if (preserveToken && key === 'token') continue;
    localStorage.removeItem(key);
  }
}

export function performLogout({ redirectTo = '/login', broadcast = true } = {}) {
  clearAuthContext();
  if (broadcast) {
    localStorage.setItem('hb:auth-sync', JSON.stringify({ type: 'logout', ts: Date.now() }));
  }
  window.dispatchEvent(new Event('hb:session-updated'));
  if (redirectTo) {
    window.location.href = redirectTo;
  }
}
